import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

export const BYDSG_MEETING_STATUSES = [
  "preview",
  "pending_confirmation",
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
] as const;
export const BydsgMeetingStatusSchema = z.enum(BYDSG_MEETING_STATUSES);
export type BydsgMeetingStatus = z.infer<typeof BydsgMeetingStatusSchema>;

const workflowRequestKey = z.string().trim().min(1).max(200).optional();

export const BYDSG_CUSTOM_ENTITY_TYPES = ["person", "project", "meeting", "weekly_update"] as const;
export const BydsgCustomEntityTypeSchema = z.enum(BYDSG_CUSTOM_ENTITY_TYPES);
export type BydsgCustomEntityType = z.infer<typeof BydsgCustomEntityTypeSchema>;

export const BYDSG_FIELD_TYPES = [
  "text",
  "number",
  "boolean",
  "date",
  "select",
  "multi_select",
] as const;
export const BydsgFieldTypeSchema = z.enum(BYDSG_FIELD_TYPES);
export type BydsgFieldType = z.infer<typeof BydsgFieldTypeSchema>;

export const BydsgCustomFieldsSchema = z.record(z.string(), z.unknown());

const bydsgFieldKey = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, "Field key must use lowercase letters, numbers, and underscores.");

export const BydsgFieldDefinitionSchema = z.object({
  id: Id,
  spaceId: Id,
  entityType: BydsgCustomEntityTypeSchema,
  key: bydsgFieldKey,
  label: z.string().trim().min(1).max(200),
  fieldType: BydsgFieldTypeSchema,
  options: z.array(z.string().trim().min(1).max(200)),
  required: z.boolean(),
  active: z.boolean(),
});
export type BydsgFieldDefinition = z.infer<typeof BydsgFieldDefinitionSchema>;

export const BydsgDefineFieldInputSchema = z.object({
  entityType: BydsgCustomEntityTypeSchema,
  key: bydsgFieldKey,
  label: z.string().trim().min(1).max(200),
  fieldType: BydsgFieldTypeSchema,
  options: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
  required: z.boolean().default(false),
  active: z.boolean().default(true),
  requestKey: workflowRequestKey,
});
export type BydsgDefineFieldInput = z.infer<typeof BydsgDefineFieldInputSchema>;

export const BydsgUpdateFieldDefinitionInputSchema = z.object({
  fieldId: Id,
  label: z.string().trim().min(1).max(200).optional(),
  fieldType: BydsgFieldTypeSchema.optional(),
  options: z.array(z.string().trim().min(1).max(200)).max(100).optional(),
  required: z.boolean().optional(),
  active: z.boolean().optional(),
  requestKey: workflowRequestKey,
});
export type BydsgUpdateFieldDefinitionInput = z.infer<typeof BydsgUpdateFieldDefinitionInputSchema>;

export const BydsgListFieldDefinitionsInputSchema = z.object({
  entityType: BydsgCustomEntityTypeSchema.optional(),
  includeInactive: z.boolean().default(false),
});
export type BydsgListFieldDefinitionsInput = z.infer<typeof BydsgListFieldDefinitionsInputSchema>;

export const BydsgSetCustomFieldsInputSchema = z.object({
  entityType: BydsgCustomEntityTypeSchema,
  entityId: Id,
  values: BydsgCustomFieldsSchema,
  requestKey: workflowRequestKey,
});
export type BydsgSetCustomFieldsInput = z.infer<typeof BydsgSetCustomFieldsInputSchema>;

export const BydsgPersonSchema = z.object({
  id: Id,
  spaceId: Id,
  name: z.string(),
  email: z.string().email().nullable(),
  department: z.string().nullable(),
  groups: z.array(z.string()),
  shortForms: z.string(),
  active: z.boolean(),
  customFields: BydsgCustomFieldsSchema,
  sourceSystem: z.string().nullable(),
  sourceId: z.string().nullable(),
});
export type BydsgPerson = z.infer<typeof BydsgPersonSchema>;

export const BydsgProjectSchema = z.object({
  id: Id,
  spaceId: Id,
  name: z.string(),
  ownerPersonId: Id.nullable(),
  department: z.string().nullable(),
  health: z.string().nullable(),
  status: z.string().nullable(),
  targetDate: z.string().nullable(),
  percentComplete: z.number().int().nullable(),
  customFields: BydsgCustomFieldsSchema,
});
export type BydsgProject = z.infer<typeof BydsgProjectSchema>;

export const BydsgMeetingSchema = z.object({
  id: Id,
  spaceId: Id,
  title: z.string(),
  meetingType: z.string(),
  purpose: z.string(),
  startAt: IsoDate,
  endAt: IsoDate,
  timezone: z.string(),
  venue: z.string(),
  status: BydsgMeetingStatusSchema,
  calendarEventId: z.string().nullable(),
  docsTabUrl: z.string().url().nullable(),
  customFields: BydsgCustomFieldsSchema,
});
export type BydsgMeeting = z.infer<typeof BydsgMeetingSchema>;

export const BydsgWeeklyUpdateSchema = z.object({
  id: Id,
  spaceId: Id,
  projectId: Id,
  reporterKey: z.string(),
  personId: Id.nullable(),
  weekStart: z.string(),
  status: z.string(),
  progressThisWeek: z.string(),
  nextSteps: z.string(),
  blockers: z.string(),
  targetDate: z.string().nullable(),
  reviewed: z.boolean(),
  submittedAt: IsoDate,
  customFields: BydsgCustomFieldsSchema,
});
export type BydsgWeeklyUpdate = z.infer<typeof BydsgWeeklyUpdateSchema>;

export const BydsgMeetingPreviewSchema = z.object({
  title: z.string().trim().min(1).max(200),
  meetingType: z.string().trim().min(1).max(80).default("HOD meeting"),
  purpose: z.string().max(2_000).default(""),
  startAt: IsoDate,
  endAt: IsoDate,
  timezone: z.string().trim().min(1).max(80).default("Asia/Singapore"),
  venue: z.string().trim().min(1).max(500).default("TBC"),
  attendeePersonIds: z.array(Id).max(200).default([]),
  guestEmails: z.array(z.string().email()).max(100).default([]),
});
export type BydsgMeetingPreview = z.infer<typeof BydsgMeetingPreviewSchema>;

export const BydsgScheduleMeetingInputSchema = BydsgMeetingPreviewSchema.extend({
  requestKey: workflowRequestKey,
});
export type BydsgScheduleMeetingInput = z.infer<typeof BydsgScheduleMeetingInputSchema>;

export const BYDSG_SOURCE_KINDS = [
  "pre_meeting_report",
  "transcript",
  "post_meeting_notes",
  "minutes_reference",
  "other",
] as const;

export const BydsgSubmitMeetingSourceInputSchema = z.object({
  meetingId: Id,
  kind: z.enum(BYDSG_SOURCE_KINDS),
  revision: z.number().int().positive(),
  content: z.string().trim().min(1).max(200_000),
  sourceHash: z.string().trim().max(200).optional(),
  requestKey: workflowRequestKey,
});
export type BydsgSubmitMeetingSourceInput = z.infer<typeof BydsgSubmitMeetingSourceInputSchema>;

export const BydsgSaveMinuteInputSchema = z.object({
  meetingId: Id,
  content: z.string().trim().min(1).max(200_000),
  sourceRevision: z.number().int().positive().optional(),
  requestKey: workflowRequestKey,
});
export type BydsgSaveMinuteInput = z.infer<typeof BydsgSaveMinuteInputSchema>;

export const BydsgSendMinuteInputSchema = z.object({
  minuteId: Id,
  to: z.array(z.string().email()).min(1).max(100),
  subject: z.string().trim().min(1).max(200),
  requestKey: workflowRequestKey,
});
export type BydsgSendMinuteInput = z.infer<typeof BydsgSendMinuteInputSchema>;

export const BydsgSaveWeeklyUpdateInputSchema = z.object({
  projectId: Id,
  reporterPersonId: Id.optional(),
  reporterKey: z.string().trim().max(200).default(""),
  weekStart: z.string().date(),
  status: z.string().trim().min(1).max(80).default("on_track"),
  progressThisWeek: z.string().max(20_000).default(""),
  nextSteps: z.string().max(20_000).default(""),
  blockers: z.string().max(20_000).default(""),
  targetDate: z.string().date().optional(),
  requestKey: workflowRequestKey,
});
export type BydsgSaveWeeklyUpdateInput = z.infer<typeof BydsgSaveWeeklyUpdateInputSchema>;

export const BydsgCreatePersonInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().email().nullable().optional(),
  department: z.string().trim().max(100).nullable().optional(),
  groups: z.array(z.string().trim().max(100)).max(20).default([]),
  shortForms: z.string().trim().max(500).default(""),
  active: z.boolean().default(true),
  requestKey: workflowRequestKey,
});
export type BydsgCreatePersonInput = z.infer<typeof BydsgCreatePersonInputSchema>;

export const BydsgUpdatePersonInputSchema = z.object({
  personId: Id,
  name: z.string().trim().min(1).max(200).optional(),
  email: z.string().email().nullable().optional(),
  department: z.string().trim().max(100).nullable().optional(),
  groups: z.array(z.string().trim().max(100)).max(20).optional(),
  shortForms: z.string().trim().max(500).optional(),
  active: z.boolean().optional(),
  requestKey: workflowRequestKey,
});
export type BydsgUpdatePersonInput = z.infer<typeof BydsgUpdatePersonInputSchema>;

export const BydsgCreateProjectInputSchema = z.object({
  name: z.string().trim().min(1).max(300),
  ownerPersonId: Id.nullable().optional(),
  department: z.string().trim().max(100).nullable().optional(),
  health: z.string().trim().max(80).nullable().optional(),
  status: z.string().trim().max(80).nullable().optional(),
  targetDate: z.string().date().nullable().optional(),
  percentComplete: z.number().int().min(0).max(100).nullable().optional(),
  requestKey: workflowRequestKey,
});
export type BydsgCreateProjectInput = z.infer<typeof BydsgCreateProjectInputSchema>;

export const BydsgUpdateProjectInputSchema = z.object({
  projectId: Id,
  name: z.string().trim().min(1).max(300).optional(),
  ownerPersonId: Id.nullable().optional(),
  department: z.string().trim().max(100).nullable().optional(),
  health: z.string().trim().max(80).nullable().optional(),
  status: z.string().trim().max(80).nullable().optional(),
  targetDate: z.string().date().nullable().optional(),
  percentComplete: z.number().int().min(0).max(100).nullable().optional(),
  requestKey: workflowRequestKey,
});
export type BydsgUpdateProjectInput = z.infer<typeof BydsgUpdateProjectInputSchema>;

export const BydsgMeetingSourceSchema = z.object({
  id: Id,
  spaceId: Id,
  meetingId: Id,
  kind: z.string(),
  revision: z.number().int().positive(),
  status: z.string(),
  content: z.string(),
  sourceHash: z.string().nullable(),
  submittedAt: IsoDate.nullable(),
  archivedAt: IsoDate.nullable(),
});
export type BydsgMeetingSource = z.infer<typeof BydsgMeetingSourceSchema>;

export const BydsgMinuteSchema = z.object({
  id: Id,
  spaceId: Id,
  meetingId: Id,
  draftRevision: z.number().int().positive(),
  status: z.string(),
  content: z.string(),
  sourceRevision: z.number().int().positive().nullable(),
  reviewId: z.string().nullable(),
  sentMessageId: z.string().nullable(),
  sentAt: IsoDate.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type BydsgMinute = z.infer<typeof BydsgMinuteSchema>;

export const BydsgSettingsSchema = z.object({
  id: Id,
  spaceId: Id,
  timezone: z.string(),
  workingHoursStart: z.string(),
  workingHoursEnd: z.string(),
  weeklyDeadlineDay: z.number().int(),
  weeklyDeadlineTime: z.string(),
  standardMeetingDay: z.number().int(),
  standardMeetingStart: z.string(),
  standardMeetingEnd: z.string(),
  defaultVenue: z.string(),
  organizerEmail: z.string().email().nullable(),
  calendarConnectionId: Id.nullable(),
  docsConnectionId: Id.nullable(),
  docsTemplateFileId: z.string().nullable(),
  mailConnectionId: Id.nullable(),
  chairPersonId: Id.nullable(),
  minuteTakerPersonId: Id.nullable(),
  instructionsVersion: z.string(),
  minutesTemplateVersion: z.string(),
});
export type BydsgSettings = z.infer<typeof BydsgSettingsSchema>;

export const BydsgUpdateSettingsInputSchema = z.object({
  timezone: z.string().trim().min(1).max(80).optional(),
  workingHoursStart: z.string().trim().max(20).optional(),
  workingHoursEnd: z.string().trim().max(20).optional(),
  weeklyDeadlineDay: z.number().int().min(1).max(7).optional(),
  weeklyDeadlineTime: z.string().trim().max(20).optional(),
  standardMeetingDay: z.number().int().min(1).max(7).optional(),
  standardMeetingStart: z.string().trim().max(20).optional(),
  standardMeetingEnd: z.string().trim().max(20).optional(),
  defaultVenue: z.string().trim().max(500).optional(),
  organizerEmail: z.string().email().nullable().optional(),
  calendarConnectionId: Id.nullable().optional(),
  docsConnectionId: Id.nullable().optional(),
  docsTemplateFileId: z.string().trim().max(500).nullable().optional(),
  mailConnectionId: Id.nullable().optional(),
  chairPersonId: Id.nullable().optional(),
  minuteTakerPersonId: Id.nullable().optional(),
});
export type BydsgUpdateSettingsInput = z.infer<typeof BydsgUpdateSettingsInputSchema>;

export const BydsgListProjectsInput = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.string().trim().max(80).optional(),
  health: z.string().trim().max(80).optional(),
});
export type BydsgListProjectsInput = z.infer<typeof BydsgListProjectsInput>;

export const BydsgListWeeklyUpdatesInput = z.object({
  projectId: Id.optional(),
  weekStart: z.string().date().optional(),
  status: z.string().trim().max(80).optional(),
  includeReviewed: z.boolean().default(true),
});
export type BydsgListWeeklyUpdatesInput = z.infer<typeof BydsgListWeeklyUpdatesInput>;

const importSource = z.object({
  sourceSystem: z.string().trim().min(1).max(80).default("notion"),
  sourceId: z.string().trim().min(1).max(500),
  sourceUrl: z.string().url().optional(),
});

export const BydsgImportBundleSchema = z.object({
  schemaVersion: z.literal(1),
  sourceSnapshotHash: z.string().trim().min(1).optional(),
  settings: z
    .object({
      timezone: z.string().default("Asia/Singapore"),
      workingHoursStart: z.string().default("09:00"),
      workingHoursEnd: z.string().default("18:00"),
      weeklyDeadlineDay: z.number().int().min(1).max(7).default(4),
      weeklyDeadlineTime: z.string().default("17:00"),
      standardMeetingDay: z.number().int().min(1).max(7).default(1),
      standardMeetingStart: z.string().default("09:00"),
      standardMeetingEnd: z.string().default("10:00"),
      defaultVenue: z.string().default("TBC"),
      organizerEmail: z.string().email().optional(),
      calendarConnectionId: z.string().optional(),
      docsConnectionId: z.string().optional(),
      docsTemplateFileId: z.string().optional(),
      mailConnectionId: z.string().optional(),
      chairSourceId: z.string().optional(),
      minuteTakerSourceId: z.string().optional(),
      instructionsVersion: z.string().default("v1"),
      minutesTemplateVersion: z.string().default("v1"),
    })
    .optional(),
  people: z.array(
    z
      .object({
        name: z.string().trim().min(1).max(200),
        email: z.string().email().optional(),
        department: z.string().max(100).optional(),
        groups: z.array(z.string().max(100)).max(20).default([]),
        shortForms: z.string().max(500).default(""),
        active: z.boolean().default(true),
      })
      .extend(importSource.shape),
  ),
  projects: z.array(
    z
      .object({
        name: z.string().trim().min(1).max(300),
        ownerSourceId: z.string().optional(),
        department: z.string().max(100).optional(),
        health: z.string().max(80).optional(),
        status: z.string().max(80).optional(),
        targetDate: z.string().date().optional(),
        percentComplete: z.number().int().min(0).max(100).optional(),
      })
      .extend(importSource.shape),
  ),
  meetings: z.array(
    z
      .object({
        title: z.string().trim().min(1).max(200),
        meetingType: z.string().max(80).default("HOD meeting"),
        purpose: z.string().max(2_000).default(""),
        startAt: IsoDate,
        endAt: IsoDate,
        timezone: z.string().default("Asia/Singapore"),
        venue: z.string().max(500).default("TBC"),
        status: z.string().max(80).default("scheduled"),
        calendarEventId: z.string().optional(),
        docsFileId: z.string().optional(),
        docsTabId: z.string().optional(),
        docsTabUrl: z.string().url().optional(),
        chairSourceId: z.string().optional(),
        minuteTakerSourceId: z.string().optional(),
        attendees: z
          .array(
            z.object({
              personSourceId: z.string().optional(),
              guestName: z.string().max(200).optional(),
              guestEmail: z.string().email().optional(),
              role: z.string().max(80).optional(),
              responseStatus: z.string().max(80).optional(),
            }),
          )
          .default([]),
        sources: z
          .array(
            z.object({
              kind: z.string().min(1).max(80),
              revision: z.number().int().positive(),
              status: z.string().max(80).default("archived"),
              content: z.string(),
              sourceHash: z.string().optional(),
            }),
          )
          .default([]),
      })
      .extend(importSource.shape),
  ),
  weeklyUpdates: z.array(
    z
      .object({
        projectSourceId: z.string().trim().min(1),
        reporterSourceId: z.string().optional(),
        reporterKey: z.string().default(""),
        weekStart: z.string().date(),
        status: z.string().max(80).default("on_track"),
        progressThisWeek: z.string().default(""),
        nextSteps: z.string().default(""),
        blockers: z.string().default(""),
        targetDate: z.string().date().optional(),
        reviewed: z.boolean().default(false),
        submittedAt: IsoDate.optional(),
      })
      .extend(importSource.shape),
  ),
});
export type BydsgImportBundle = z.infer<typeof BydsgImportBundleSchema>;
